import React, { useState, useEffect } from 'react';
import { CreditCard, Check, X, Crown, Zap } from 'lucide-react';

const SubscriptionPage = () => {
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [canceling, setCanceling] = useState(false);

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch current subscription, plans, and payment history
      const [currentRes, plansRes, paymentsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/subscriptions/current`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/subscriptions/plans`),
        fetch(`${process.env.REACT_APP_API_URL}/api/subscriptions/payments`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      const [current, plansList, payments] = await Promise.all([
        currentRes.json(),
        plansRes.json(),
        paymentsRes.json()
      ]);
      
      console.log('Subscription data:', { current, plansList, payments });
      
      setCurrentSubscription(current);
      setPlans(plansList);
      setPaymentHistory(payments);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    try {
      setUpgrading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/subscriptions/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      // Check if response has content before parsing JSON
      const responseText = await response.text();
      console.log('Response text:', responseText);
      
      if (!responseText) {
        throw new Error(`Empty response from server (HTTP ${response.status})`);
      }
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 100)}...`);
      }
      
      console.log('Parsed checkout session data:', data);
      
      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}: Failed to create checkout session`);
      }
      
      if (data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Failed to create checkout session - no URL returned');
      }
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      alert('Failed to start upgrade process. Please try again.');
    } finally {
      setUpgrading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your Pro subscription? You will lose AI access at the end of your billing period.')) {
      return;
    }
    
    try {
      setCanceling(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/subscriptions/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        await fetchSubscriptionData(); // Refresh subscription data
        alert('Subscription canceled. You will retain Pro features until the end of your billing period.');
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      alert('Failed to cancel subscription. Please try again.');
    } finally {
      setCanceling(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp || timestamp === 0) return 'N/A';
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const formatAmount = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-6"></div>
          <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const isPro = currentSubscription?.planId === 'pro';
  const isCanceled = Boolean(currentSubscription?.cancelAtPeriodEnd);

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Subscription Management</h1>
        <p className="text-gray-600 dark:text-gray-300">Manage your EDX Red subscription and billing</p>
      </div>

      {/* Current Subscription Status */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Crown className={`mr-2 h-5 w-5 ${isPro ? 'text-yellow-500' : 'text-gray-400'}`} />
          Current Plan
        </h2>
        
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                isPro 
                  ? 'bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {currentSubscription?.name || 'Free'}
              </span>
              {isPro && (
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  ${(currentSubscription?.price / 100).toFixed(2)}/month
                </span>
              )}
            </div>
            
            {isPro && (currentSubscription?.currentPeriodEnd > 0 || isCanceled) && (
              <div className="mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                {currentSubscription?.currentPeriodEnd && currentSubscription.currentPeriodEnd > 0 && (
                  <div>Next billing: {formatDate(currentSubscription.currentPeriodEnd)}</div>
                )}
                {isCanceled && (
                  <div className="text-orange-600 font-medium">
                    ⚠️ Cancels at period end
                  </div>
                )}
              </div>
            )}
            
            {currentSubscription?.features && (
              <div className="mt-3">
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">Plan features:</p>
                <div className="flex flex-wrap gap-2">
                  {JSON.parse(currentSubscription.features).map((feature, index) => (
                    <span key={index} className="inline-flex items-center px-2 py-1 rounded-md bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-xs">
                      <Check className="w-3 h-3 mr-1" />
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {!isPro && (
            <button
              onClick={handleUpgrade}
              disabled={upgrading}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {upgrading ? 'Processing...' : 'Upgrade to Pro'}
            </button>
          )}
          
          {isPro && !isCanceled && (
            <div className="space-y-2">
              <button
                onClick={handleCancel}
                disabled={canceling}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {canceling ? 'Canceling...' : 'Cancel Subscription'}
              </button>
              <a
                href="https://billing.stripe.com/p/login/28EcN6a2l6wXbr8grye7m00"
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-6 py-2 rounded-lg font-medium transition-colors text-center"
              >
                Manage subscription through Stripe
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Available Plans */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Available Plans</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {plans.map((plan) => (
            <div key={plan.id} className={`relative border-2 rounded-lg p-6 ${
              currentSubscription?.planId === plan.id 
                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' 
                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
            }`}>
              {currentSubscription?.planId === plan.id && (
                <div className="absolute -top-3 left-6 bg-purple-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                  Current Plan
                </div>
              )}
              
              <div className="text-center mb-4">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center justify-center">
                  {plan.name}
                  {plan.id === 'pro' && <Zap className="ml-2 h-5 w-5 text-yellow-500" />}
                </h3>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                  {plan.price === 0 ? 'Free' : `$${(plan.price / 100).toFixed(2)}`}
                  {plan.price > 0 && <span className="text-lg text-gray-600 dark:text-gray-400">/month</span>}
                </div>
              </div>
              
              <div className="space-y-3">
                {JSON.parse(plan.features).map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <Check className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>
              
              {plan.id === 'pro' && !isPro && (
                <button
                  onClick={handleUpgrade}
                  disabled={upgrading}
                  className="w-full mt-6 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {upgrading ? 'Processing...' : 'Upgrade Now'}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Payment History */}
      {paymentHistory.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <CreditCard className="mr-2 h-5 w-5" />
            Payment History
          </h2>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {paymentHistory.map((payment) => (
                  <tr key={payment.id}>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                      {formatDate(payment.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                      {payment.description || 'Payment'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                      {formatAmount(payment.amount)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        payment.status === 'succeeded' 
                          ? 'bg-green-100 text-green-800'
                          : payment.status === 'failed'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {payment.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;